import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goToConsultation = (e) => {
    e.preventDefault();
    setOpen(false);
    const scroll = () =>
      document.getElementById("consultation")?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (location.pathname === "/") {
      scroll();
    } else {
      navigate("/");
      setTimeout(scroll, 200);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-cream/85 backdrop-blur-xl border-b border-navy/10"
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 md:h-24 flex items-center justify-between gap-6">
        <Link to="/" className="group flex items-center gap-3 md:gap-4" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <span className="block h-12 w-12 md:h-14 md:w-14 rounded-full overflow-hidden border border-navy/15 bg-white shrink-0">
            <img src="/annwil-logo.png" alt="Annwil Law Ja logo" className="w-full h-full object-cover" />
          </span>
          <span className="flex flex-col">
            <span className="font-serif text-2xl md:text-3xl font-medium text-navy leading-none tracking-tight">
              Annwil <span className="italic text-gold-hover">Law</span> Ja
            </span>
            <span className="text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground mt-1">
              Your Trusted Legal Partner
            </span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-10">
          {LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `relative text-sm font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                  isActive ? "text-gold-hover" : "text-navy hover:text-gold-hover"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <button
            onClick={goToConsultation}
            data-testid="nav-book-consultation-button"
            className="rounded-full bg-navy text-cream text-sm font-semibold px-6 py-3 transition-colors duration-300 hover:bg-gold hover:text-navy"
          >
            Book a Consultation
          </button>
        </div>

        <button
          data-testid="mobile-menu-button"
          className="lg:hidden text-navy p-2"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-navy/10 bg-cream/95 backdrop-blur-xl"
            data-testid="mobile-menu-panel"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}-link`}
                  className={({ isActive }) =>
                    `font-serif text-3xl ${isActive ? "text-gold-hover italic" : "text-navy"}`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <button
                onClick={goToConsultation}
                data-testid="mobile-nav-book-consultation-button"
                className="mt-2 rounded-full bg-navy text-cream text-center text-sm font-semibold px-6 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
              >
                Book a Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
